import { useApolloClient } from '@apollo/client';
import { Roles } from '@seek/profile-read-ui';
import { useTranslations } from '@vocab/react';
import { Alert, Text, Stack } from 'braid-design-system';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Card } from 'src/components/Card/Card';
import { CardAction } from 'src/components/Card/CardAction/CardAction.tsx';
import { FormDrawer } from 'src/components/FormDrawer/FormDrawer';
import { LoadingCardItem } from 'src/components/LoadingBlock/LoadingBlock';
import { CAREER_HISTORY, CREATE_RESUME_PAGE } from 'src/components/Router';
import { useId } from 'src/components/Router/useId';
import { useMode } from 'src/components/Router/useMode';
import { useNavigateRoute } from 'src/components/Router/useNavigateRoute';
import { usePage } from 'src/components/Router/usePage';
import { EDIT, ADD } from 'src/consts';
import { CAREER_HISTORY_FORM } from 'src/context/FormContext';
import { analyticsClient } from 'src/modules/analytics/index.ts';
import { useAppConfig } from 'src/modules/appConfig';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import {
  retryParent,
  getFailedActionsForParent,
} from 'src/modules/redux-blend';
import { companyReviewsLinkClicked } from 'src/reducers/location';
import { UnconfirmedDataType, type ConfirmRoleInput } from 'src/types/graphql';

import { trackUnconfirmedImpression } from '../../../../modules/impression-tracking';
import { setRouteAsTouched } from '../../CreateResume/actionCreators.ts';
import { usePersonalDetails } from '../../PersonalDetails/data.ts';
import { EditCareerHistory } from '../EditCareerHistory/EditCareerHistory';
import {
  useAddUnconfirmedRole,
  useGetConfirmedRoles,
  useGetUnconfirmedRoles,
  useRejectUnconfirmedRole,
} from '../data';

import translations from './.vocab';

export const ReadCareerHistory = () => {
  const { zone, brand } = useAppConfig();
  const dispatch = useDispatch();
  const navigate = useNavigateRoute();
  const apolloClient = useApolloClient();

  const blend = useTypedSelector((state) => state.blend);
  const showRetry =
    getFailedActionsForParent({ blend }, CAREER_HISTORY).length > 0;
  const retryErrors = () => dispatch(retryParent(CAREER_HISTORY));
  const retryAction = showRetry ? retryErrors : undefined;

  const [addUnconfirmedRoleFn] = useAddUnconfirmedRole(ADD);
  const [rejectUnconfirmedRoleFn] = useRejectUnconfirmedRole();

  const { t } = useTranslations(translations);
  const page = usePage();
  const mode = useMode(CAREER_HISTORY);
  const roleId = useId();

  const emptyCardText = t(
    'The more you let employers know about your experience, the more you can stand out.',
  );

  const onReviewClick = ({
    companyName,
    url,
  }: {
    companyName: string;
    url: string;
  }) => {
    dispatch(companyReviewsLinkClicked(companyName));
    window.open(url);
  };

  const [drawerTitle, setDrawerTitle] = useState<string>('');

  useEffect(() => {
    if (mode === EDIT) {
      if (roleId) {
        setDrawerTitle(t('Edit role'));
      } else {
        setDrawerTitle(t('Add role'));
      }
    }
  }, [mode, t, roleId]);

  const editRoleFn = (id: string, confirmed: boolean) => {
    analyticsClient.cardOpened({
      ...(!confirmed ? { origin: 'resume parsing nudge' } : {}),
      currentPageSection: 'career history',
      currentPageSectionState: 'edit',
    });

    if (page === CREATE_RESUME_PAGE) {
      setRouteAsTouched(CAREER_HISTORY);
    }

    navigate({
      card: CAREER_HISTORY,
      mode: EDIT,
      id,
    });
  };

  const { unconfirmedRoles, loading: unconfirmedRolesLoading } =
    useGetUnconfirmedRoles();

  const {
    confirmedRoles,
    loading: confirmedRolesLoading,
    error: confirmedRolesError,
  } = useGetConfirmedRoles();

  const confirmedRolesPending = confirmedRolesLoading || confirmedRolesError;

  // Ensure personal details are loaded before rendering to prevent
  // trackUnconfirmedRoleImpression from firing too early
  const { loading: personalDetailsLoading } = usePersonalDetails();

  const isLoading = Boolean(confirmedRolesPending || unconfirmedRolesLoading);

  const isEmpty = [...confirmedRoles, ...unconfirmedRoles].length === 0;

  return (
    <>
      <Card
        name={CAREER_HISTORY}
        retryAction={retryAction}
        title={t('Career history')}
        emptyCardText={emptyCardText}
        isEmpty={isEmpty}
        cardAction={
          <CardAction
            card={CAREER_HISTORY}
            mode={EDIT}
            data={{ automation: 'career-history-add' }}
            actionText={t('Add role')}
            isEmpty={isEmpty}
            onClick={() => {
              analyticsClient.cardOpened({
                currentPageSection: 'career history',
                currentPageSectionState: 'add',
              });
            }}
          />
        }
        loading={isLoading}
        loader={
          <Stack space="small">
            <LoadingCardItem />
            <LoadingCardItem />
            <LoadingCardItem />
          </Stack>
        }
      >
        <Stack space="large">
          <Stack space="small">
            {unconfirmedRoles.length > 0 && (
              <>
                <Alert tone="promote">
                  <Text>
                    {t('We found these roles in your resumé. Add...', {
                      brand,
                    })}
                  </Text>
                </Alert>
                <Roles
                  displayVariant="card"
                  roles={unconfirmedRoles.slice(0, 10).map((role) => ({
                    ...role,
                    editRole: editRoleFn,
                    confirmed: false,
                    addUnconfirmedRole: (_id, contentRead) => {
                      addUnconfirmedRoleFn(
                        {
                          role: role as ConfirmRoleInput, // TODO: casting to avoid sending deprecated `function` and `industry` fields. Can remove once removed from graph schema.
                        },
                        { contentRead },
                      );
                    },
                    rejectUnconfirmedRole: (id) =>
                      rejectUnconfirmedRoleFn({
                        role: {
                          id,
                          dataType: UnconfirmedDataType.CareerHistory,
                          tracking: role.tracking,
                        },
                      }),
                    trackUnconfirmedRoleImpression: (roleData) =>
                      personalDetailsLoading
                        ? null
                        : trackUnconfirmedImpression(
                            { type: 'role', data: roleData },
                            apolloClient,
                          ),
                    stillInRole: !role.to,
                    zone,
                  }))}
                />
              </>
            )}

            {confirmedRoles.length > 0 && (
              <Roles
                displayVariant="card"
                roles={confirmedRoles.map((role) => ({
                  ...role,
                  editRole: editRoleFn,
                  showReviewLink: [
                    'anz-1',
                    'anz-2',
                    'asia-1',
                    'asia-3',
                    'asia-4',
                    'asia-5',
                    'asia-6',
                    'asia-7',
                  ].includes(zone),
                  onReviewClick,
                  confirmed: true,
                  stillInRole: !role.to,
                  zone,
                }))}
              />
            )}
          </Stack>
        </Stack>
      </Card>
      <FormDrawer
        title={drawerTitle}
        component={EditCareerHistory}
        card={CAREER_HISTORY}
        formName={CAREER_HISTORY_FORM}
      />
    </>
  );
};
